import { getAuthCookie, canReadWriteCookies } from '../cookies';
import { getGraphQLClient } from '@simplywallst/services';
import type { GraphQLClient, RequestDocument } from 'graphql-request';

interface SWSGQLHeaders {
  'x-sws-pro-api': string;
}

let globalPreferredLanguage: string | undefined;

type SWSGQLErrorType = {
  message?: string;
  path?: string[];
  extensions?: {
    code: string;
    statusCode: number;
    serviceName: string;
    traceId: string;
  };
};

export const isErrors = (errors: unknown): errors is SWSGQLErrorType[] => {
  if (!Array.isArray(errors)) return false;
  if (errors.length === 0) return false;
  return errors.every((e) => {
    if (typeof e !== 'object') return false;
    if (e === null) return false;
    return 'message' in e && 'extensions' in e;
  });
};

export class SWSGQLError extends Error {
  errors: SWSGQLErrorType[] = [];
  status: number;
  code: string = 'UNKNOWN_ERROR';
  message: string = 'Unknown error';

  constructor(errors?: SWSGQLErrorType[], statusCode = 500) {
    super();
    this.status = statusCode;

    if (errors && errors.length > 0) {
      this.errors = errors;

      const [first] = errors;
      const { extensions, message } = first;

      if (extensions) {
        this.status = extensions.statusCode;
        this.code = extensions.code;
      }

      this.message = message ?? 'Unknown error';
    }
  }
}

export const isSWSGQLError = (error: unknown): error is SWSGQLError => {
  if (error === null) return false;
  if (error instanceof Error) {
    if ('errors' in error && 'code' in error && 'status' in error) {
      const { code, status, errors } = error;
      return (
        typeof code === 'string' &&
        typeof status === 'number' &&
        Array.isArray(errors)
      );
    }
  }
  return false;
};

export function setPreferredLanguage(lang = 'en-US') {
  globalPreferredLanguage = lang;
}

const getHeader = (additionalHeaders: Partial<SWSGQLHeaders> = {}) => {
  let preferredLanguage: string | undefined;
  try {
    preferredLanguage = window.navigator.language;
  } catch {
    preferredLanguage = globalPreferredLanguage;
  }

  return {
    'apollographql-client-name': RUNTIME_ENV === 'native' ? 'mobile' : 'web',
    ...(typeof preferredLanguage === 'string'
      ? { 'Accept-Language': preferredLanguage }
      : {}),
    ...additionalHeaders,
  };
};

export const handleError = (error: unknown) => {
  if (error instanceof Error) {
    const { response } = JSON.parse(JSON.stringify(error));
    if (typeof response === 'object' && 'errors' in response) {
      const { errors } = response;
      if (isErrors(errors)) {
        throw new SWSGQLError(errors);
      }
    }
  }
  throw new Error('Error unknown');
};

interface Config {
  headers?: HeadersInit;
  baseURL?: string;
}
function makeGQLClient(
  gqlClient: (config: Config) => GraphQLClient = getGraphQLClient
) {
  return {
    request: async <T>(
      query: RequestDocument,
      variables = {},
      additionalHeaders: Partial<SWSGQLHeaders> = {}
    ) => {
      const client = gqlClient({
        headers: getHeader(additionalHeaders),
        baseURL:
          typeof SWS_INTERNAL_GQL_URL !== 'undefined'
            ? SWS_INTERNAL_GQL_URL
            : undefined,
      });

      const response = await client
        .request<T>(query, variables)
        .catch(handleError);

      return response;
    },
    requestWithAuth: async <T>(
      query: RequestDocument,
      variables = {},
      additionalHeaders: Partial<SWSGQLHeaders> = {}
    ) => {
      const client = gqlClient({
        headers: getHeader(additionalHeaders),
      });
      if (!canReadWriteCookies()) throw new Error('Cannot read cookies');
      const auth = await getAuthCookie();
      if (typeof auth === 'undefined') {
        throw new Error('Auth cookie is invalid');
      }
      client.setHeader('Authorization', `Bearer ${auth.accessToken}`);

      const response = await client
        .request<T>(query, variables)
        .catch(handleError);

      return response;
    },
  };
}

const defaultClient = makeGQLClient();
const { request, requestWithAuth } = defaultClient;

export { request, requestWithAuth };
